import { DOCUMENT } from '@angular/common';
import { ApplicationRef, inject, Injectable, InjectionToken, NgZone } from '@angular/core';

import { comb } from 'email-comb';
import juice from 'juice';
import { switchMap, take } from 'rxjs';

import { MAILGUN_BLOCK_SCOPE_MARKER } from '../mailgun/constants';
import { TemplateRendererConfig } from '../providers/template-renderer-config';

export type NodeToUnwrap = HTMLElement;

export const PostRenderCleanupServiceToken = new InjectionToken<PostRenderCleanupService>(
  'PostRenderCleanupService',
);

@Injectable()
export class NoopPostRenderCleanupService {
  public register() {
    return void 0;
  }
}

@Injectable()
export class PostRenderCleanupService {
  public config = inject(TemplateRendererConfig);
  public doc = inject(DOCUMENT);
  public zone = inject(NgZone);
  public appRef = inject(ApplicationRef);
  public elements = new Set<NodeToUnwrap>();

  constructor() {
    this.elements.add(this.doc.documentElement.querySelector('email-root')!);
    this.runCleanupWhenStable();
  }

  public register(element: NodeToUnwrap) {
    this.elements.add(element);
  }

  public runCleanup() {
    this.unwrapElements();
    this.removeTags(['ng-component', 'router-outlet', 'script']);
    this.collapseStyleTags();
    this.optimize();
  }

  private runCleanupWhenStable() {
    this.appRef.isStable
      .pipe(
        switchMap(() => this.zone.onStable),
        take(1),
      )
      .subscribe(() => this.runCleanup());
  }

  private removeTags(selectors: string[]) {
    selectors.forEach(selector =>
      Array.from(this.doc.documentElement.querySelectorAll(selector)).forEach(
        s => s.parentNode?.removeChild(s),
      ),
    );
  }

  private unwrapElements() {
    Array.from(this.elements).forEach(el => {
      if (el.childNodes.length) {
        el.replaceWith(...Array.from(el.childNodes));
      } else {
        el.parentNode?.removeChild(el);
      }
      this.elements.delete(el);
    });
  }

  private getComments(context: any): Comment[] {
    const foundComments = [];
    const elementPath = [context];
    while (elementPath.length > 0) {
      const el = elementPath.pop();
      for (let i = 0; i < el.childNodes.length; i++) {
        const node = el.childNodes[i];
        if (node.nodeType === Node.COMMENT_NODE) {
          foundComments.push(node);
        } else {
          elementPath.push(node);
        }
      }
    }

    return foundComments;
  }

  private collapseStyleTags() {
    const styles = Array.from(this.doc.documentElement.querySelectorAll('style'));

    const collapsed = styles.reduce((agg, s, i) => `${agg}${s.textContent}`, '');
    styles.forEach(s => s.parentNode?.removeChild(s));

    const sTag = this.doc.createElement('style');
    sTag.textContent = collapsed;

    this.doc.documentElement.querySelector('head')?.appendChild(sTag);
  }

  private optimize() {
    const html = this.doc.documentElement.outerHTML;

    const { result } = comb(
      juice(html, {
        removeStyleTags: true,
        preserveMediaQueries: true,
      }),
      {
        removeHTMLComments: true,
        doNotRemoveHTMLCommentsWhoseOpeningTagContains: ['mailgun'],
        backend: [
          {
            heads: '{{',
            tails: '}}',
          },
          {
            heads: '{{{',
            tails: '}}}',
          },
        ],
        whitelist: ['.web-*', '.preview*'],
      },
    );

    this.doc.documentElement.innerHTML = result;

    this.getComments(this.doc.documentElement).forEach(comment => {
      const token = comment.textContent!.replace(`${MAILGUN_BLOCK_SCOPE_MARKER}`, '');
      comment.replaceWith(this.doc.createTextNode(token));
    });
  }
}
